import { useEffect, useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { toast } from "react-toastify";
import EntityRoutingService from "src/modules/data-protection/apis/EntityRoutingService";
import useFunctionRoutingDefaults, {
  getAiGovernanceRoutingUser,
  getItSecurityRoutingUser,
  toRoutingUserOption,
} from "src/modules/data-protection/pages/admin/administration/settings/components/routing/hooks/useFunctionRoutingDefaults";
import {
  SOFTWARE_CREATION_ENTITY_ROUTING_QUERY_KEY,
  getAiGovernanceDefaultUserId,
  getItSecurityDefaultUserId,
  getSelectedLegalEntityIds,
  hasEmbeddedRoutingDefaults,
  resolveDefaultOption,
  unwrapRoutingPayload,
} from "../helpers/functionDefaults";

const useSoftwareCreationFunctionDefaults = ({
  selectedLegalEntities,
  t,
}) => {
  const {
    isFunctionsLoading,
    aiGovernanceTitle,
    itSecurityTitle,
    aiGovernanceOptions,
    itSecurityOptions,
  } = useFunctionRoutingDefaults(t, { notifyError: true });

  const [aiGovernanceUser, setAiGovernanceUser] = useState(null);
  const [itSecurityUser, setItSecurityUser] = useState(null);
  const [aiGovernanceEdited, setAiGovernanceEdited] = useState(false);
  const [itSecurityEdited, setItSecurityEdited] = useState(false);

  const selectedEntityIds = useMemo(
    () => getSelectedLegalEntityIds(selectedLegalEntities),
    [selectedLegalEntities]
  );

  const entitiesById = useMemo(() => {
    const map = {};

    (selectedLegalEntities || []).forEach((entity) => {
      const id = Number(entity?.id);
      if (Number.isFinite(id) && id > 0) {
        map[id] = entity;
      }
    });

    return map;
  }, [selectedLegalEntities]);

  const idsNeedingFetch = useMemo(
    () =>
      selectedEntityIds.filter(
        (id) => !hasEmbeddedRoutingDefaults(entitiesById[id])
      ),
    [entitiesById, selectedEntityIds]
  );

  const routingQueries = useQueries({
    queries: idsNeedingFetch.map((id) => ({
      queryKey: [SOFTWARE_CREATION_ENTITY_ROUTING_QUERY_KEY, id],
      queryFn: async () => {
        const service = EntityRoutingService.getInstance();
        return service.fetchRoutingDetails(id);
      },
      enabled: idsNeedingFetch.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      onError: () =>
        toast(t("An error occurred while fetching routing details."), {
          type: "error",
        }),
    })),
  });

  const isRoutingLoading = routingQueries.some((query) => query.isLoading);

  const entityPayloads = useMemo(
    () =>
      selectedEntityIds.map((id) => {
        const entity = entitiesById[id];

        if (hasEmbeddedRoutingDefaults(entity)) {
          return entity;
        }

        const queryIndex = idsNeedingFetch.indexOf(id);
        const queryData = routingQueries[queryIndex]?.data;

        return unwrapRoutingPayload(queryData) || queryData || null;
      }),
    [entitiesById, idsNeedingFetch, routingQueries, selectedEntityIds]
  );

  const resolvedAiGovernanceOption = useMemo(
    () =>
      resolveDefaultOption({
        payloads: entityPayloads,
        getUserId: (payload) => {
          const fromMember = toRoutingUserOption(
            getAiGovernanceRoutingUser(payload)
          );
          if (fromMember?.value != null) {
            return fromMember.value;
          }
          return getAiGovernanceDefaultUserId(
            payload,
            getAiGovernanceRoutingUser
          );
        },
        memberOptions: aiGovernanceOptions,
      }),
    [aiGovernanceOptions, entityPayloads]
  );

  const resolvedItSecurityOption = useMemo(
    () =>
      resolveDefaultOption({
        payloads: entityPayloads,
        getUserId: (payload) => {
          const fromMember = toRoutingUserOption(
            getItSecurityRoutingUser(payload)
          );
          if (fromMember?.value != null) {
            return fromMember.value;
          }
          return getItSecurityDefaultUserId(
            payload,
            getItSecurityRoutingUser
          );
        },
        memberOptions: itSecurityOptions,
      }),
    [entityPayloads, itSecurityOptions]
  );

  const selectedEntityKey = selectedEntityIds.join(",");
  const appliedKeyRef = useRef({
    entities: null,
    ai: false,
    it: false,
  });

  useEffect(() => {
    if (isRoutingLoading || isFunctionsLoading) {
      return;
    }

    if (appliedKeyRef.current.entities !== selectedEntityKey) {
      appliedKeyRef.current = {
        entities: selectedEntityKey,
        ai: false,
        it: false,
      };
    }

    if (!aiGovernanceEdited && !appliedKeyRef.current.ai) {
      setAiGovernanceUser(resolvedAiGovernanceOption);
      appliedKeyRef.current.ai = true;
    }

    if (!itSecurityEdited && !appliedKeyRef.current.it) {
      setItSecurityUser(resolvedItSecurityOption);
      appliedKeyRef.current.it = true;
    }
  }, [
    aiGovernanceEdited,
    isFunctionsLoading,
    isRoutingLoading,
    itSecurityEdited,
    resolvedAiGovernanceOption,
    resolvedItSecurityOption,
    selectedEntityKey,
  ]);

  const handleAiGovernanceChange = (option) => {
    setAiGovernanceUser(option);
    setAiGovernanceEdited(option != null);
  };

  const handleItSecurityChange = (option) => {
    setItSecurityUser(option);
    setItSecurityEdited(option != null);
  };

  return {
    aiGovernanceTitle,
    itSecurityTitle,
    aiGovernanceOptions,
    itSecurityOptions,
    aiGovernanceUser,
    itSecurityUser,
    isFunctionsLoading,
    isRoutingLoading,
    onAiGovernanceChange: handleAiGovernanceChange,
    onItSecurityChange: handleItSecurityChange,
  };
};

export default useSoftwareCreationFunctionDefaults;
